import { IResponse, consumer, postFile } from "@bemap/utils"

// Types based on the backend entities
export interface Course {
  id: number;
  title: string;
  description: string;
  instructor: string;
  category: 'partner' | 'analyst';
  level: 'beginner' | 'intermediate' | 'advanced';
  completed: boolean;
  progress: number;
  thumbnailUrl?: string;
  durationMinutes: number;
  price?: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  modules?: Module[];
}

export interface Module {
  id: number;
  title: string;
  description: string;
  completed: boolean;
  progress: number;
  order: number;
  durationMinutes: number;
  active: boolean;
  courseId: number;
  createdAt: string;
  updatedAt: string;
  contents?: Content[];
}

export interface Content {
  id: number;
  title: string;
  completed: boolean;
  active: boolean;
  duration: string;
  type: 'video' | 'document' | 'quiz';
  data: VideoData | DocumentData | QuizData | null;
  order: number;
  moduleId: number;
  createdAt: string;
  updatedAt: string;
  comments?: Comment[];
  questions?: Question[];
}

export interface VideoData {
  videoUrl: string;
  thumbnailUrl?: string;
  duration?: number;
}

export interface DocumentData {
  markdown: string;
}

export interface QuizQuestion {
  id: string;
  question: string;
  type: 'single' | 'multiple' | 'text';
  options?: string[];
  correctAnswer?: string | string[];
  explanation?: string;
}

export interface QuizData {
  title: string;
  description: string;
  questions: QuizQuestion[];
  passingScore: number;
  allowRetake: boolean;
  showCorrectAnswers: boolean;
}

export interface Comment {
  id: number;
  content: string;
  userId: number;
  contentId: number;
  parentId?: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
  replies?: Comment[];
}

export interface Question {
  id: number;
  content: string;
  userId: number;
  contentId: number;
  isAnswered: boolean;
  adminReply?: string;
  adminReplyAt?: string;
  adminReplyBy?: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
  adminUser?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface CourseStudent {
  id: number;
  courseId: number;
  userId: number;
  status: 'enrolled' | 'in_progress' | 'completed' | 'dropped' | 'suspended';
  overallProgress: number;
  completedModules: number;
  totalModules: number;
  completedContents: number;
  totalContents: number;
  enrolledAt?: string;
  startedAt?: string;
  completedAt?: string;
  lastAccessedAt?: string;
  moduleProgress?: Record<number, {
    completed: boolean;
    progress: number;
    completedContents: number;
    totalContents: number;
    lastAccessedAt: string;
  }>;
  contentProgress?: Record<number, {
    completed: boolean;
    lastAccessedAt: string;
    timeSpent: number;
  }>;
  totalTimeSpent: number;
  grade?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  course?: Course;
  user?: {
    id: number;
    name: string;
    email: string;
  };
}

// Course API calls
export const getCourses = async (): Promise<IResponse> => {
  const url = `/university/courses`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getCourseById = async (id: number): Promise<IResponse> => {
  const url = `/university/courses/${id}`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const createCourse = async (coursePayload: FormData): Promise<IResponse> => {
  return postFile(`/university/courses`, coursePayload);
}

export const updateCourse = async (id: number, coursePayload: FormData): Promise<IResponse> => {
  return postFile(`/university/courses`, coursePayload, id);
}

export const deleteCourse = async (id: number): Promise<IResponse> => {
  const url = `/university/courses/${id}`;

  try {
    return await consumer('DELETE', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Content API calls
export const getContentById = async (id: number): Promise<IResponse> => {
  const url = `/university/content/${id}`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getContentComments = async (contentId: number): Promise<IResponse> => {
  const url = `/university/content/${contentId}/comments`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getContentQuestions = async (contentId: number): Promise<IResponse> => {
  const url = `/university/content/${contentId}/questions`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Module API calls
export const createModule = async (payload: Partial<Module>): Promise<IResponse> => {
  const url = `/university/modules`;

  try {
    return await consumer('POST', url, payload);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const updateModule = async (id: number, payload: Partial<Module>): Promise<IResponse> => {
  const url = `/university/modules/${id}`;

  try {
    return await consumer('PUT', url, payload);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const deleteModule = async (id: number): Promise<IResponse> => {
  const url = `/university/modules/${id}`;

  try {
    return await consumer('DELETE', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Content CRUD API calls
export const createContent = async (payload: Partial<Content>): Promise<IResponse> => {
  const url = `/university/content`;

  try {
    return await consumer('POST', url, payload);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const updateContent = async (id: number, payload: Partial<Content>): Promise<IResponse> => {
  const url = `/university/content/${id}`;

  try {
    return await consumer('PUT', url, payload);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const deleteContent = async (id: number): Promise<IResponse> => {
  const url = `/university/content/${id}`;

  try {
    return await consumer('DELETE', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Comment API calls
export const createComment = async (payload: Partial<Comment>): Promise<IResponse> => {
  const url = `/university/comments`;

  try {
    return await consumer('POST', url, payload);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const updateComment = async (id: number, payload: Partial<Comment>): Promise<IResponse> => {
  const url = `/university/comments/${id}`;

  try {
    return await consumer('PUT', url, payload);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const deleteComment = async (id: number): Promise<IResponse> => {
  const url = `/university/comments/${id}`;

  try {
    return await consumer('DELETE', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Question API calls
export const createQuestion = async (payload: Partial<Question>): Promise<IResponse> => {
  const url = `/university/questions`;

  try {
    return await consumer('POST', url, payload);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const updateQuestion = async (id: number, payload: Partial<Question>): Promise<IResponse> => {
  const url = `/university/questions/${id}`;

  try {
    return await consumer('PUT', url, payload);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const deleteQuestion = async (id: number): Promise<IResponse> => {
  const url = `/university/questions/${id}`;

  try {
    return await consumer('DELETE', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// CourseStudent API calls
export const enrollStudent = async (courseId: number, userId: number): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/enroll/${userId}`;

  try {
    return await consumer('POST', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getStudentEnrollment = async (courseId: number, userId: number): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/students/${userId}`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const updateStudentProgress = async (courseId: number, userId: number, payload: Partial<CourseStudent>): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/students/${userId}/progress`;

  try {
    return await consumer('PUT', url, payload);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const markContentCompleted = async (courseId: number, userId: number, contentId: number): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/students/${userId}/content/${contentId}/complete`;

  try {
    return await consumer('POST', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// New completion endpoints for admin
export const getContentCompletion = async (
  courseStudentId: number,
  contentId: number
): Promise<IResponse> => {
  const url = `/university/content/${contentId}/completions?courseStudentId=${courseStudentId}`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getModuleCompletion = async (
  courseStudentId: number,
  moduleId: number
): Promise<IResponse> => {
  const url = `/university/modules/${moduleId}/completions?courseStudentId=${courseStudentId}`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getCourseCompletions = async (
  courseId: number,
  studentId: number
): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/students/${studentId}/completions`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getCourseAnalytics = async (
  courseId: number,
  studentId: number
): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/students/${studentId}/analytics`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Get course completions by courseId (for current user)
export const getCourseCompletionsByCourseId = async (courseId: number): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/completions`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Get course analytics by courseId (for current user)
export const getCourseAnalyticsByCourseId = async (courseId: number): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/analytics`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getStudentCourses = async (userId: number): Promise<IResponse> => {
  const url = `/university/students/${userId}/courses`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getCourseStudents = async (courseId: number): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/students`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getAllStudents = async (): Promise<IResponse> => {
  const url = `/university/students`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getCourseStats = async (courseId: number): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/stats`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const unenrollStudent = async (courseId: number, userId: number): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/students/${userId}`;

  try {
    return await consumer('DELETE', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
}; 